<template>
  <div>
    <div class="section-hero-subpage bg-color-octrary">
      <video autoplay loop muted playsinline width="640" preload="metadata" poster="/frontend/assets/images/og-image.jpg">
        <source src="/frontend/assets/videos/video.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <div class="container">
        <div class="paragraph--normal text-uppercase text-color-quinary mb-2">
          <span class="notranslate" translate="no">SELECTED</span> CARS
        </div>
        <h1 class="heading--0 text-uppercase text-color-quinary border-bottom-quinary padding-bottom-2 margin-bottom-3-lg">
          {{ brandName }} <span class="fw-light">Cars<br />for Export</span>
        </h1>
        <div class="row">
          <div class="col-lg-7">
            <p class="paragraph--xlarge text-color-senary">
              {{ heroDescription }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Brand Info Section -->
    <div class="section-small bg-color-tertiary">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-2 col-md-3 text-center mb-lg-0 mb-4" v-if="brand && brand.logo_path">
            <img :src="'/storage/' + brand.logo_path" :alt="brandName + ' logo'" class="brand-logo-large" />
          </div>
          <div :class="brand && brand.logo_path ? 'col-lg-10 col-md-9' : 'col-12'">
            <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-3">
              Buy {{ brandName }} from Europe — Export Worldwide
            </h2>
            <p class="paragraph--large text-color-senary">
              {{ brandDescription }}
            </p>
            <div class="row mt-4">
              <div class="col-md-3 col-6 mb-3">
                <div class="stat-box">
                  <div class="stat-box--number">{{ activeCars.length }}</div>
                  <div class="stat-box--label">Available Now</div>
                </div>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="stat-box">
                  <div class="stat-box--number">{{ soldCars.length }}</div>
                  <div class="stat-box--label">Successfully Exported</div>
                </div>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="stat-box">
                  <div class="stat-box--number">16+</div>
                  <div class="stat-box--label">Countries Served</div>
                </div>
              </div>
              <div class="col-md-3 col-6 mb-3">
                <div class="stat-box">
                  <div class="stat-box--number">EUR</div>
                  <div class="stat-box--label">Competitive Pricing</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Cars -->
    <div class="section-small bg-color-primary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          {{ brandName }} Cars Available for Export
        </h2>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-light" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <template v-else-if="activeCars.length > 0">
          <CarItem v-for="car in activeCars" :key="car.id" :car="car" />
        </template>

        <div v-else class="text-center py-5">
          <p class="paragraph--large text-color-senary">
            No {{ brandName }} cars currently in stock. Contact us to source your {{ brandName }} from European dealerships.
          </p>
          <router-link to="/car-on-request" class="btn btn--normal btn--primary text-uppercase mt-3">
            Request a {{ brandName }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Recently Sold -->
    <div v-if="soldCars.length > 0" class="section-small bg-color-tertiary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          Recently Exported {{ brandName }} Cars
        </h2>
        <CarItem v-for="car in soldCars.slice(0, 6)" :key="car.id" :car="car" />
      </div>
    </div>

    <!-- Why Buy Brand from Europe -->
    <div class="section-small bg-color-quaternary">
      <div class="container">
        <h2 class="heading--4 fw-light text-color-quinary text-uppercase border-bottom-quinary padding-bottom-3-lg padding-bottom-2 margin-bottom-5-lg margin-bottom-3">
          Why Buy {{ brandName }} from Europe?
        </h2>
        <div class="row">
          <div class="col-lg-4 mb-lg-0 mb-4">
            <div class="block-item">
              <div class="paragraph--xxxlarge text-color-quinary lh-1 mb-3">Best Prices</div>
              <p class="paragraph--normal text-color-senary">
                European {{ brandName }} prices are often significantly lower than Middle East or Asian markets. We source from 92 partner dealerships across Europe to find the best deal for you.
              </p>
            </div>
          </div>
          <div class="col-lg-4 mb-lg-0 mb-4">
            <div class="block-item">
              <div class="paragraph--xxxlarge text-color-quinary lh-1 mb-3">Full Specs</div>
              <p class="paragraph--normal text-color-senary">
                European-spec {{ brandName }} vehicles come fully loaded with the latest features, safety systems and technology packages. We help you choose the right configuration for your market.
              </p>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="block-item">
              <div class="paragraph--xxxlarge text-color-quinary lh-1 mb-3">Door-to-Door</div>
              <p class="paragraph--normal text-color-senary">
                We handle everything: sourcing, documentation, customs clearance and shipping. Your {{ brandName }} arrives at your door anywhere in the world within 2-6 weeks.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CTA -->
    <div class="section-small bg-color-primary text-center">
      <div class="container">
        <h2 class="heading--3 text-color-quinary text-uppercase mb-4">
          Looking for a specific {{ brandName }}?
        </h2>
        <p class="paragraph--xlarge text-color-senary mb-5">
          Tell us what you need. We source any {{ brandName }} model from European dealerships.
        </p>
        <div class="d-flex justify-content-center gap-3 flex-wrap">
          <router-link to="/car-on-request" class="btn btn--normal btn--primary text-uppercase">
            Request a {{ brandName }}
          </router-link>
          <router-link to="/contacts" class="btn btn--normal btn--primary text-uppercase">
            Contact Us
          </router-link>
        </div>
      </div>
    </div>

    <AppMarquee />
  </div>
</template>

<script>
import AppMarquee from '../components/AppMarquee.vue';
import CarItem from '../components/CarItem.vue';
import axios from 'axios';

const brandDescriptions = {
  'bmw': 'Selected Cars is your trusted source for BMW vehicles exported directly from Europe. From the sporty M series to the luxurious 7 Series and versatile X range, we provide access to the full BMW lineup at competitive European prices with worldwide delivery.',
  'mercedes-benz': 'Export Mercedes-Benz luxury vehicles from Europe with Selected Cars. S-Class, G-Class, AMG, Maybach, EQS — we source every Mercedes model from authorized European dealers and deliver worldwide with full documentation.',
  'porsche': 'Buy Porsche from Europe at the best prices. 911, Cayenne, Panamera, Taycan, Macan — Selected Cars sources directly from European Porsche centers and exports to clients in the Middle East, Asia, Africa and beyond.',
  'audi': 'Export Audi vehicles from Europe worldwide. RS models, Q-series SUVs, e-tron electric, A8 luxury — Selected Cars sources the full Audi range from 92 European partner dealerships at competitive prices.',
  'ferrari': 'Source Ferrari supercars from Europe with Selected Cars. New and pre-owned Ferrari Roma, SF90 Stradale, 296 GTB, Purosangue — exported from European dealerships to clients worldwide with complete documentation.',
  'lamborghini': 'Export Lamborghini from Europe at competitive prices. Urus, Huracan, Revuelto — Selected Cars is your trusted broker for Lamborghini vehicles sourced from European dealers and delivered worldwide.',
  'land rover': 'Buy Land Rover and Range Rover from Europe for export. Range Rover, Range Rover Sport, Defender, Velar — sourced from European dealers at competitive prices and shipped door-to-door worldwide.',
  'range rover': 'Export Range Rover vehicles direct from Europe. The ultimate luxury SUV sourced from European Land Rover dealers at competitive prices, delivered to your door in the Middle East, Asia, Africa and beyond.',
  'bentley': 'Source Bentley luxury vehicles from Europe with Selected Cars. Continental GT, Flying Spur, Bentayga — exported from European dealers to discerning clients worldwide.',
  'rolls-royce': 'Export Rolls-Royce from Europe. Ghost, Phantom, Spectre, Cullinan — Selected Cars sources the pinnacle of luxury from European dealers and delivers worldwide.',
  'maserati': 'Buy Maserati from Europe for worldwide export. GranTurismo, Grecale, MC20, Levante — sourced from European Maserati dealerships at competitive prices.',
  'volkswagen': 'Export Volkswagen vehicles from Europe. Touareg, Tiguan, Golf R, ID series — Selected Cars sources the full VW range from European dealers at competitive prices for international buyers.',
};

const brandHeroDescriptions = {
  'bmw': 'Source BMW vehicles directly from European dealerships. Competitive prices, full European spec, worldwide delivery to 16+ countries.',
  'mercedes-benz': 'Export Mercedes-Benz from Europe — S-Class, G-Class, AMG, Maybach. Delivered door-to-door to the Middle East, Asia, Africa and beyond.',
  'porsche': 'Buy Porsche directly from Europe at the best prices. 911, Cayenne, Panamera, Taycan — exported worldwide from European Porsche centers.',
  'ferrari': 'Source Ferrari supercars from European dealerships. New and pre-owned, exported worldwide with full documentation.',
  'lamborghini': 'Export Lamborghini from Europe. Urus, Huracan, Revuelto — sourced from European dealers at competitive prices.',
  'land rover': 'Buy Range Rover and Land Rover from Europe. Exported door-to-door from European dealers to clients in 16+ countries.',
  'audi': 'Source Audi from European dealerships. RS, Q-series, e-tron — competitive European prices with worldwide delivery.',
};

export default {
  components: { AppMarquee, CarItem },
  data() {
    return {
      brand: null,
      allCars: [],
      loading: true
    };
  },
  computed: {
    brandSlug() {
      // Normalize slug: decode, lowercase, replace spaces/pluses/underscores with dashes
      const raw = this.$route.params.slug ? decodeURIComponent(this.$route.params.slug) : '';
      return raw.toLowerCase().replace(/[\s+_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
    },
    brandName() {
      if (this.brand) return this.brand.name;
      // Capitalize slug fallback
      return this.brandSlug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },
    heroDescription() {
      return brandHeroDescriptions[this.brandSlug] ||
        `Source ${this.brandName} vehicles directly from European dealerships at competitive prices. Worldwide delivery to 16+ countries including UAE, Saudi Arabia, Qatar, China and more.`;
    },
    brandDescription() {
      return brandDescriptions[this.brandSlug] ||
        `Selected Cars is your trusted source for ${this.brandName} vehicles exported directly from Europe. We source from 92 partner dealerships across Europe and deliver worldwide to clients in the Middle East, Asia, Africa and beyond. Competitive European pricing, full documentation and door-to-door shipping included.`;
    },
    activeCars() {
      return this.allCars.filter(c => c.status === 'Available');
    },
    soldCars() {
      return this.allCars.filter(c => c.status === 'Sold');
    }
  },
  methods: {
    parseVehicleFields(v) {
      const brand = (v.make || '').trim();
      let model = (v.model || '').trim();
      let trimLine = (v.trim_line || '').trim();
      if (model.includes('*')) {
        const parts = model.split('*').map(p => p.trim()).filter(Boolean);
        let firstPart = parts[0];
        if (brand && firstPart.toLowerCase().startsWith(brand.toLowerCase())) {
          firstPart = firstPart.substring(brand.length).trim();
        }
        model = firstPart || parts[0];
        trimLine = parts.slice(1).join(', ');
      }
      if (trimLine.includes('*')) {
        const parts = trimLine.split('*').map(p => p.trim()).filter(Boolean);
        let startIdx = 0;
        if (parts[0] && brand) {
          const first = parts[0].toLowerCase();
          if (first.startsWith(brand.toLowerCase())) startIdx = 1;
        }
        trimLine = parts.slice(startIdx).join(', ');
      }
      return { model, trimLine };
    },
    mapVehicle(v) {
      const parsed = this.parseVehicleFields(v);
      return {
        id: v.id,
        brand: v.make,
        model: parsed.model,
        year: v.year,
        mileage: v.mileage,
        nettoPrice: v.netto_price,
        grossPrice: v.gross_price,
        trimLine: parsed.trimLine,
        image: v.primary_image ? v.primary_image.url : null,
        status: v.status === 'active' ? 'Available' : v.status === 'sold' ? 'Sold' : v.status
      };
    },
    async loadData() {
      this.loading = true;
      try {
        const [brandsRes, vehiclesRes] = await Promise.all([
          axios.get('/api/brands'),
          axios.get('/api/vehicles')
        ]);

        // Find brand by slug match
        const brands = brandsRes.data;
        this.brand = brands.find(b => b.name.toLowerCase() === this.brandSlug) ||
                     brands.find(b => b.name.toLowerCase().replace(/\s+/g, '-') === this.brandSlug);

        // Get all vehicles for this brand
        const raw = Array.isArray(vehiclesRes.data) ? vehiclesRes.data : (vehiclesRes.data.data || []);
        const matchName = this.brand ? this.brand.name : this.brandSlug;
        this.allCars = raw
          .filter(v => v.make && v.make.toLowerCase() === matchName.toLowerCase())
          .map(this.mapVehicle);

      } catch (e) {
        console.error('Error loading brand data:', e);
      } finally {
        this.loading = false;
      }

      // Set SEO after data loads
      import('../seo.js').then(({ setPageSeo, slugify, carUrl }) => setPageSeo({
        title: `Buy ${this.brandName} from Europe | Export ${this.brandName} Worldwide | Selected Cars`,
        description: `Export ${this.brandName} vehicles from Europe. ${this.activeCars.length} cars available now. Competitive European prices, door-to-door delivery to UAE, Saudi Arabia, Qatar, China, Africa. Trusted European car exporter.`,
        canonical: `https://selected.sk/brands/${this.brandSlug}`,
        keywords: `${this.brandName} export europe, buy ${this.brandName} from europe, ${this.brandName} for sale international, ${this.brandName} export worldwide, ${this.brandName} europe price, import ${this.brandName} from europe, ${this.brandName} dealer europe export, ${this.brandName} ship to middle east, ${this.brandName} export UAE dubai`,
        breadcrumbs: [
          { name: 'Home', url: '/' },
          { name: 'Brands', url: '/brands' },
          { name: this.brandName, url: `/brands/${this.brandSlug}` }
        ],
        schema: {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": `${this.brandName} Cars for Export from Europe`,
          "description": `Browse ${this.brandName} vehicles available for worldwide export from Selected Cars Europe.`,
          "url": `https://selected.sk/brands/${this.brandSlug}`,
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": this.activeCars.length,
            "itemListElement": this.activeCars.slice(0, 10).map((car, i) => ({
              "@type": "ListItem",
              "position": i + 1,
              "item": {
                "@type": "Car",
                "name": `${car.year} ${car.brand} ${car.model}`,
                "url": carUrl(car)
              }
            }))
          }
        }
      }));
    }
  },
  mounted() {
    this.loadData();
  },
  watch: {
    '$route.params.slug'() {
      this.loadData();
    }
  }
};
</script>

<style scoped>
.brand-logo-large {
  max-width: 120px;
  max-height: 120px;
  filter: brightness(0) invert(1);
  opacity: 0.8;
}
.stat-box {
  text-align: center;
  padding: 1.5rem 1rem;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
}
.stat-box--number {
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  margin-bottom: 0.3rem;
}
.stat-box--label {
  font-size: 0.7rem;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}
</style>
